import { TRUE_FALSE_OPTION_LABELS } from "@/components/storyboard/quiz/TrueFalseAnswerButton";
import { getTextParts } from "@/components/storyboard/quiz/utils/quizUtils";
import { splitLeadingFence } from "@/components/storyboard/code/codeUtils";
import {
  buildClozeItem,
  buildMatchingItem,
  buildOptionsItem,
} from "@/components/storyboard/quiz/review/buildReviewItem";
import type {
  QuizCodeTrueFalseEntryData,
  QuizDropdownEntryData,
  QuizMatchingEntryData,
} from "@/types/storyboard";
import type { QuizReviewItem } from "@/lib/quizReview";

/**
 * Review items for quizzes whose verdict comes from the server. The same
 * builders serve two moments: right after a submit (the answer is still in
 * component state) and when a finished quiz is restored from `validation_data`
 * (the answer is the stored `userInput`), so both show the same review.
 */

const parseJson = (userInput: string): unknown => {
  try {
    return JSON.parse(userInput);
  } catch {
    return null;
  }
};

/** Stored `userInput` of a dropdown answer: one selected option per gap. */
export const parseDropdownAnswers = (userInput: string): string[] => {
  const parsed = parseJson(userInput);
  return Array.isArray(parsed) ? parsed.map((answer) => String(answer)) : [];
};

/** Stored `userInput` of a matching answer, as second part → chosen first part. */
export const parseMatchingAssignments = (
  userInput: string
): Record<string, string> => {
  const parsed = parseJson(userInput);
  if (!Array.isArray(parsed)) return {};
  const assignments: Record<string, string> = {};
  parsed.forEach((pair) => {
    if (
      pair &&
      typeof pair.firstPart === "string" &&
      typeof pair.secondPart === "string"
    ) {
      assignments[pair.secondPart] = pair.firstPart;
    }
  });
  return assignments;
};

export const buildDropdownEntryReview = (
  entry: QuizDropdownEntryData,
  userAnswers: string[],
  isCorrect: boolean
): QuizReviewItem => {
  const numberOfGaps = getTextParts(entry.clozeText, entry.gapMark).length - 1;
  return buildClozeItem({
    prompt: entry.instruction,
    clozeText: entry.clozeText,
    gapMark: entry.gapMark,
    gaps: Array.from({ length: numberOfGaps }, (_, i) => {
      const correctAnswer = entry.gaps?.[i]?.correctAnswer ?? "";
      return {
        userAnswer: userAnswers[i] ?? "",
        correctAnswer,
        correct: userAnswers[i] === correctAnswer,
      };
    }),
    isCorrect,
    feedback: entry.userFeedback,
  });
};

/** `assignments` maps each second part to the first part the learner chose. */
export const buildMatchingEntryReview = (
  entry: QuizMatchingEntryData,
  assignments: Record<string, string | null>,
  isCorrect: boolean
): QuizReviewItem =>
  buildMatchingItem({
    prompt: entry.instruction,
    rows: (entry.pairs ?? []).map((pair) => {
      const userTerm = assignments[pair.secondPart] ?? null;
      return {
        secondPart: pair.secondPart,
        userTerm,
        correctTerm: pair.firstPart,
        correct: userTerm === pair.firstPart,
      };
    }),
    isCorrect,
    feedback: entry.userFeedback,
  });

/**
 * Storyboards published before the `codeSnippet` field existed packed the code
 * into `statement` as a leading markdown fence.
 */
export const resolveCodeTrueFalseEntry = (
  entry: QuizCodeTrueFalseEntryData
): { codeSnippet: string | null; statementText: string } => {
  const legacyFence = entry.codeSnippet
    ? null
    : splitLeadingFence(entry.statement);
  return {
    codeSnippet: entry.codeSnippet || legacyFence?.fence || null,
    statementText: legacyFence?.rest ?? entry.statement,
  };
};

/**
 * The server verdict decides which option was right, so the review does not
 * depend on the entry still carrying `correctAnswer`.
 */
export const buildCodeTrueFalseEntryReview = (
  entry: QuizCodeTrueFalseEntryData,
  selectedAnswer: boolean,
  isCorrect: boolean
): QuizReviewItem => {
  const { codeSnippet, statementText } = resolveCodeTrueFalseEntry(entry);
  const correctValue = isCorrect ? selectedAnswer : !selectedAnswer;
  return buildOptionsItem({
    prompt: statementText,
    codeSnippet: codeSnippet ?? undefined,
    options: [
      {
        label: TRUE_FALSE_OPTION_LABELS.true,
        selected: selectedAnswer,
        correct: correctValue,
      },
      {
        label: TRUE_FALSE_OPTION_LABELS.false,
        selected: !selectedAnswer,
        correct: !correctValue,
      },
    ],
    isCorrect,
    feedback: entry.userFeedback,
  });
};
